// Dashboard — the studio at a glance (DESIGN-SYNC "Dashboard — Take the
// design", with the owner's 2026-10-15 decisions). Which widgets show, and whose
// rows they add up, is the role's: Organisation → Access sets both, and the
// server applies them (20261015220000_dashboard.sql). This page lays out what
// comes back and computes nothing of its own.
//
// Not here, on purpose: a change since last period (nothing keeps history yet),
// the skill-by-week heatmap and skill-gap panel (Capacity's and Projects'), cost,
// margin and pay. Money is a deal value, only where the server sends one.
import { useEffect, useReducer, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { canOpen } from '../lib/roles';
import { env } from '../lib/env';
import { useOrgDates } from '../lib/orgSettings';
import { fmtDate, fmtDayKey, fmtDayRange, orgDateKey } from '../lib/dates';
import { fmtDuration } from '../lib/duration';
import { fmtINR, fmtINRFull } from '../lib/money';
import { useBelow } from '../lib/useMediaQuery';
import { jiraIssueUrl, stageIndex, stageName } from '../lib/projects';
import { REPORT_STATUS } from '../lib/clientReports';
import { issueHref } from '../lib/issues';
import {
  canTimeProjects,
  loadRunningTimer,
  setRunningTimer,
  useRunningTimer,
} from '../lib/projectTimer';
import {
  loadMyDashboard,
  loadWidget,
  SCOPE_LABEL,
  type DashProject,
  type MyDashboard,
  type WidgetData,
  type WidgetKey,
} from '../lib/dashboard';
import { Avatar, AvatarGroup, Badge, Mono, Progress, ProjectID } from '../components/primitives';
import { KpiGrid, KpiTile } from '../components/domain';
import { Card } from '../components/card';
import { EmptyState, LoadError } from '../components/feedback';
import { Countdown, MiniStages, StateBadge } from '../components/projects';

type Loaded = { [K in WidgetKey]?: WidgetData[K] };

const MAIN: WidgetKey[] = ['projects', 'tasks_due'];
const SIDE: WidgetKey[] = ['timer', 'week_time', 'client_reports', 'away'];

const hours = (s: number) => fmtDuration(s, 'decimal');

export function DashboardScreen() {
  const { session, perms } = useAuth();
  const uid = session?.user?.id;
  const { tz, dateFormat } = useOrgDates();
  const today = orgDateKey(Date.now(), tz);
  const phone = useBelow('md');
  const wide = !useBelow('lg');
  const [me, setMe] = useState<MyDashboard | null | undefined | 'loading'>('loading');
  const [data, setData] = useState<Loaded>({});
  const [failed, setFailed] = useState<WidgetKey[]>([]);
  const [tries, retry] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    let live = true;
    void (async () => {
      const d = await loadMyDashboard();
      if (!live) return;
      setMe(d);
      if (!d) return;
      const keys = d.widgets.filter((k) => k !== 'timer');
      const got = await Promise.all(keys.map((k) => loadWidget(k)));
      if (!live) return;
      setData(Object.fromEntries(keys.map((k, i) => [k, got[i] ?? undefined])) as Loaded);
      setFailed(keys.filter((_, i) => got[i] === undefined));
    })();
    return () => {
      live = false;
    };
  }, [tries]);

  // The pill keeps the running timer live; read it once here too, so the card
  // is right even before the pill has.
  const showsTimer = me !== 'loading' && !!me?.widgets.includes('timer');
  useEffect(() => {
    if (!showsTimer || !uid) return;
    void loadRunningTimer(uid).then((t) => t !== undefined && setRunningTimer(t));
  }, [showsTimer, uid]);

  if (!supabase) {
    return (
      <div style={{ padding: 40, color: 'var(--hf-ink-3)' }}>
        Supabase isn’t configured — set <Mono>VITE_SUPABASE_URL</Mono> /{' '}
        <Mono>VITE_SUPABASE_ANON_KEY</Mono>.
      </div>
    );
  }

  const pad = phone ? 'var(--hf-space-4)' : '20px 24px';
  if (me === 'loading')
    return <div style={{ padding: pad, color: 'var(--hf-ink-3)' }}>Loading…</div>;
  if (me === undefined)
    return (
      <div style={{ padding: pad }}>
        <LoadError what="your dashboard" onRetry={retry} />
      </div>
    );
  if (!me || me.widgets.length === 0)
    return (
      <div style={{ padding: pad }}>
        <EmptyState title="Nothing on your dashboard yet">
          An owner or admin chooses the widgets each role sees, in Organisation → Access.
        </EmptyState>
      </div>
    );

  const has = (k: WidgetKey) => me.widgets.includes(k);
  const tiles = data.project_tiles;
  const week = data.week_time;
  const card = (k: WidgetKey): ReactNode => {
    if (failed.includes(k))
      return (
        <Card key={k} label={TITLES[k]}>
          <LoadError what={TITLES[k].toLowerCase()} onRetry={retry} />
        </Card>
      );
    switch (k) {
      case 'projects':
        return (
          <ProjectsCard
            key={k}
            rows={data.projects}
            today={today}
            dateFormat={dateFormat}
            open={canOpen(perms, 'projects')}
            phone={phone}
          />
        );
      case 'tasks_due':
        return (
          <TasksCard key={k} rows={data.tasks_due} today={today} others={me.scope !== 'own'} />
        );
      case 'timer':
        return <TimerCard key={k} canTime={canTimeProjects(perms)} />;
      case 'week_time':
        return <WeekCard key={k} week={week} />;
      case 'client_reports':
        return <DigestCard key={k} rows={data.client_reports} open={canOpen(perms, 'reports')} />;
      case 'away':
        return <AwayCard key={k} rows={data.away} />;
      default:
        return null;
    }
  };
  const main = MAIN.filter(has).map(card);
  const side = SIDE.filter(has).map(card);
  const column: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 };

  return (
    <div style={{ padding: pad, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
        {SCOPE_LABEL[me.scope]}
      </div>

      {(has('project_tiles') || has('week_time')) && (
        <KpiGrid>
          {has('project_tiles') && (
            <>
              <KpiTile
                label="In flight"
                value={tiles ? String(tiles.in_flight) : null}
                note="projects not yet shipped"
              />
              <KpiTile
                label="At risk"
                value={tiles ? String(tiles.at_risk) : null}
                note={tiles?.blocked ? `${tiles.blocked} blocked as well` : 'by status or health'}
              />
              <KpiTile
                label="Due in 14 days"
                value={tiles ? String(tiles.due_soon) : null}
                note="delivery date in the next two weeks"
              />
              <KpiTile
                label="Overdue"
                value={tiles ? String(tiles.overdue) : null}
                note="delivery date passed, not shipped"
              />
              {tiles?.deal_value != null && (
                <KpiTile
                  label="Deal value in flight"
                  value={fmtINR(tiles.deal_value)}
                  note={`${tiles.deals} ${tiles.deals === 1 ? 'project' : 'projects'} with a deal`}
                />
              )}
            </>
          )}
          {has('week_time') && (
            <KpiTile
              label="Project hours this week"
              value={week ? hours(week.seconds) : null}
              unit="h"
              note={
                week
                  ? `of ${hours(week.worked_seconds)}h worked · ${hours(week.billable_seconds)}h billable`
                  : undefined
              }
            />
          )}
        </KpiGrid>
      )}
      {failed.includes('project_tiles') && (
        <LoadError what="the project headlines" onRetry={retry} />
      )}

      {main.length > 0 && side.length > 0 && wide ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 340px',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div style={column}>{main}</div>
          <aside aria-label="At a glance" style={column}>
            {side}
          </aside>
        </div>
      ) : (
        <div style={column}>
          {/* One column: the timer first, as the side column's top. */}
          {side.slice(0, has('timer') ? 1 : 0)}
          {main}
          {side.slice(has('timer') ? 1 : 0)}
        </div>
      )}
    </div>
  );
}

const TITLES: Record<WidgetKey, string> = {
  project_tiles: 'Project headlines',
  projects: 'Projects in flight',
  tasks_due: 'Work items due',
  week_time: 'Time this week',
  timer: 'Your timer',
  client_reports: 'Client reports',
  away: 'Who’s away',
};

const meta = { fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' } as const;
const ellipsis = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
} as const;
const rowLine = (i: number): CSSProperties => ({
  borderTop: i ? '1px solid var(--hf-rule)' : 0,
});
const Loading = () => <div style={meta}>Loading…</div>;

// ── Projects ─────────────────────────────────────────────────────────────────
const ProjectsCard = ({
  rows,
  today,
  dateFormat,
  open,
  phone,
}: {
  rows?: DashProject[];
  today: string;
  dateFormat: string;
  open: boolean;
  phone: boolean;
}) => {
  const money = !!rows?.some((p) => p.deal_value != null);
  const cols = phone
    ? 'minmax(0, 1fr) auto'
    : `minmax(64px, 0.5fr) minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(92px, 0.8fr)${money ? ' minmax(80px, 0.7fr)' : ''}`;
  const head = [
    'ID',
    'Project',
    'Team',
    'Stage',
    'Progress',
    'Delivery',
    ...(money ? ['Deal'] : []),
  ];
  return (
    <Card
      label={TITLES.projects}
      right={rows && <span style={meta}>{rows.length}</span>}
      // The table runs to the card's edges; the heading keeps the card's padding.
      style={{ paddingBottom: 0, overflow: 'hidden' }}
    >
      {!rows ? (
        <div style={{ paddingBottom: 16 }}>
          <Loading />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ paddingBottom: 8 }}>
          <EmptyState title="No projects in flight">
            Projects you lead or are on appear here until they ship.
          </EmptyState>
        </div>
      ) : (
        <>
          {!phone && (
            <div
              aria-hidden="true"
              style={{
                display: 'grid',
                gridTemplateColumns: cols,
                gap: 12,
                margin: '0 -18px',
                padding: '8px 18px',
                borderTop: '1px solid var(--hf-rule)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                ...meta,
              }}
            >
              {head.map((h) => (
                <span key={h}>{h}</span>
              ))}
            </div>
          )}
          <ul style={{ listStyle: 'none', margin: '0 -18px', padding: 0 }}>
            {rows.map((p) => {
              const step = stageIndex(p);
              const delivery = (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Mono style={{ fontSize: 11.5, color: 'var(--hf-ink)' }}>
                    {p.deadline ? fmtDate(p.deadline, dateFormat) : 'No date'}
                  </Mono>
                  <Countdown deadline={p.deadline} today={today} shipped={false} />
                </span>
              );
              const name = (
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ ...ellipsis, color: 'var(--hf-ink)' }}>{p.name}</span>
                    <StateBadge state={p.state} />
                  </span>
                  <span style={meta}>
                    {phone && p.code ? `${p.code} · ` : ''}
                    {p.client ?? 'Internal'}
                  </span>
                </span>
              );
              const cells = phone ? (
                <>
                  {name}
                  {delivery}
                </>
              ) : (
                <>
                  <ProjectID id={p.code ?? '—'} />
                  {name}
                  {p.team.length ? (
                    <span>
                      <AvatarGroup names={p.team} max={3} size={22} />
                      <span className="hf-sr-only">{p.team.join(', ')}</span>
                    </span>
                  ) : (
                    <span style={meta}>No team yet</span>
                  )}
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--hf-ink-2)' }}>
                      {stageName(step)}
                    </span>
                    <MiniStages idx={step} />
                  </span>
                  <Progress value={p.progress} size={4} showLabel label={`${p.name} progress`} />
                  {delivery}
                  {money && (
                    <Mono style={{ fontSize: 11.5 }}>
                      {p.deal_value != null ? fmtINRFull(p.deal_value) : '—'}
                    </Mono>
                  )}
                </>
              );
              const style: CSSProperties = {
                display: 'grid',
                gridTemplateColumns: cols,
                gap: 12,
                alignItems: 'center',
                padding: '12px 18px',
                color: 'inherit',
                textDecoration: 'none',
                fontSize: 13,
              };
              return (
                <li key={p.id} style={{ borderTop: '1px solid var(--hf-rule)' }}>
                  {open ? (
                    <Link to={`/project?id=${p.id}`} className="hf-row-link" style={style}>
                      {cells}
                    </Link>
                  ) : (
                    <div style={style}>{cells}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
};

// ── Work items due ───────────────────────────────────────────────────────────
const TasksCard = ({
  rows,
  today,
  others,
}: {
  rows?: WidgetData['tasks_due'];
  today: string;
  /** Not only your own: say whose each one is. */
  others: boolean;
}) => (
  <Card label={TITLES.tasks_due} right={<span style={meta}>open, due within 14 days</span>}>
    {!rows ? (
      <Loading />
    ) : rows.length === 0 ? (
      <EmptyState title="Nothing due in the next two weeks" />
    ) : (
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((t, i) => {
          // A Jira issue opens in Jira; a native work item opens in Hyprflow.
          const url = t.jira_issue_id ? jiraIssueUrl(env.jiraSiteUrl, t.code) : null;
          const title = <span style={{ ...ellipsis, color: 'var(--hf-ink)' }}>{t.title}</span>;
          return (
            <li
              key={t.id}
              style={{
                ...rowLine(i),
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                gap: 12,
                alignItems: 'center',
                padding: '10px 0',
                fontSize: 13,
              }}
            >
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  {t.code && <Mono style={{ fontSize: 11 }}>{t.code}</Mono>}
                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ minWidth: 0, display: 'flex' }}
                    >
                      {title}
                    </a>
                  ) : t.jira_issue_id ? (
                    title
                  ) : (
                    <Link
                      to={issueHref(t.project_id, t.code)}
                      style={{ minWidth: 0, display: 'flex' }}
                    >
                      {title}
                    </Link>
                  )}
                </span>
                <span style={{ ...meta, ...ellipsis }}>
                  {t.project}
                  {t.jira_status ? ` · ${t.jira_status}` : ''}
                  {others && t.assignees.length ? ` · ${t.assignees.join(', ')}` : ''}
                </span>
              </span>
              <span
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}
              >
                <Mono style={{ fontSize: 11.5 }}>{fmtDayKey(t.due_date)}</Mono>
                <Countdown deadline={t.due_date} today={today} shipped={false} />
              </span>
            </li>
          );
        })}
      </ul>
    )}
  </Card>
);

// ── Your timer ───────────────────────────────────────────────────────────────
const TimerCard = ({ canTime }: { canTime: boolean }) => {
  const t = useRunningTimer();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!t) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [t]);
  return (
    <Card label={TITLES.timer}>
      {t ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              {t.project?.code && <ProjectID id={t.project.code} />}
              <Badge tone="accent" dot>
                Running
              </Badge>
            </div>
            <div style={{ ...ellipsis, fontSize: 14, color: 'var(--hf-ink)' }}>
              {t.task ? t.task.title : (t.project?.name ?? 'Project')}
            </div>
            {t.task && <div style={{ ...meta, ...ellipsis }}>{t.project?.name}</div>}
          </div>
          <Mono style={{ fontSize: 22, color: 'var(--hf-accent-text)' }}>
            {fmtDuration((now - Date.parse(t.started_at)) / 1000, 'clock')}
          </Mono>
        </div>
      ) : (
        <EmptyState title="No timer running">
          {canTime
            ? 'Start one from the timer in the top bar.'
            : 'Your role doesn’t time projects.'}
        </EmptyState>
      )}
    </Card>
  );
};

// ── Time this week ───────────────────────────────────────────────────────────
const WeekCard = ({ week }: { week?: WidgetData['week_time'] }) => (
  <Card
    label={TITLES.week_time}
    right={week && <span style={meta}>from {fmtDayKey(week.week_start, { weekday: true })}</span>}
  >
    {!week ? (
      <Loading />
    ) : week.projects.length === 0 ? (
      <EmptyState title="No project time logged this week">
        A day counts once it is checked out.
      </EmptyState>
    ) : (
      <>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {week.projects.map((p, i) => (
            <li key={p.project_id} style={{ ...rowLine(i), padding: '8px 0' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  fontSize: 13,
                  marginBottom: 6,
                }}
              >
                <span style={{ ...ellipsis, color: 'var(--hf-ink)' }}>{p.name}</span>
                <Mono style={{ fontSize: 11.5 }}>{hours(p.seconds)}h</Mono>
              </div>
              <Progress
                value={week.seconds ? (p.seconds / week.seconds) * 100 : 0}
                size={4}
                label={`${p.name}: share of this week’s project time`}
                meter
              />
            </li>
          ))}
        </ul>
        <div style={{ ...meta, marginTop: 8 }}>
          Logged days only: today counts once it is checked out.
        </div>
      </>
    )}
  </Card>
);

// ── Client reports ───────────────────────────────────────────────────────────
const DigestCard = ({ rows, open }: { rows?: WidgetData['client_reports']; open: boolean }) => (
  <Card label={TITLES.client_reports} right={<span style={meta}>last 7 days</span>}>
    {!rows ? (
      <Loading />
    ) : rows.length === 0 ? (
      <EmptyState title="No client reports this week" />
    ) : (
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((r, i) => {
          const st = REPORT_STATUS[r.status];
          const body = (
            <>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...ellipsis, flex: 1, color: 'var(--hf-ink)', fontSize: 13 }}>
                  {r.project}
                </span>
                <Badge tone={st.tone} dot>
                  {st.label}
                </Badge>
              </span>
              <span style={meta}>
                {[r.code, fmtDayKey(r.report_date), r.can_review ? 'yours to review' : null]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </>
          );
          const style: CSSProperties = {
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            padding: '9px 0',
            color: 'inherit',
            textDecoration: 'none',
          };
          return (
            <li key={r.id} style={rowLine(i)}>
              {open ? (
                <Link to="/reports" className="hf-row-link" style={style}>
                  {body}
                </Link>
              ) : (
                <div style={style}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    )}
  </Card>
);

// ── Who's away ───────────────────────────────────────────────────────────────
const AwayCard = ({ rows }: { rows?: WidgetData['away'] }) => (
  <Card label={TITLES.away} right={<span style={meta}>next two weeks</span>}>
    {!rows ? (
      <Loading />
    ) : rows.length === 0 ? (
      <EmptyState title="Nobody is away in the next two weeks" />
    ) : (
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((a, i) => (
          <li
            key={`${a.profile_id}-${a.start_date}`}
            style={{
              ...rowLine(i),
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 0',
            }}
          >
            <Avatar name={a.name ?? '—'} size={24} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ ...ellipsis, display: 'block', fontSize: 13, color: 'var(--hf-ink)' }}>
                {a.name ?? 'Teammate'}
              </span>
              <span style={meta}>
                {fmtDayRange(a.start_date, a.end_date)}
                {a.half_day ? ' · half day' : ''}
              </span>
            </span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);
